import { DialogRef } from "@angular/cdk/dialog";
import { Component, computed, inject, input, signal } from "@angular/core";
import { rxResource } from "@angular/core/rxjs-interop";
import { combineLatest, firstValueFrom, map } from "rxjs";
import { Ticket } from "src/app/_models";
import { ModalService, OperationService, TicketService } from "src/app/_services";

@Component({
  selector: "hawk-batch-operation-form",
  templateUrl: "./batch-operation-form.component.html",
  standalone: true,
  host: {
    class: "flex flex-col gap-4 p-4",
  },
})
export class BatchOperationFormComponent {
  public static open(modal: ModalService, options: { ticketIds: number[] }) {
    return modal.open<BatchOperationFormComponent, Ticket[]>(BatchOperationFormComponent, {
      inputs: options,
      minHeight: "200px",
    });
  }
  private ticketService = inject(TicketService);
  private operationService = inject(OperationService);
  private dialogRef = inject(DialogRef);
  public ticketIds = input<number[]>([]);

  protected operationsResource = rxResource({
    request: this.ticketIds,
    loader: ({ request }) => {
      return combineLatest(
        request.map((ticketId) => {
          return this.ticketService.getOperations(String(ticketId));
        }),
      ).pipe(
        map((list) => {
          return list.flat();
        }),
      );
    },
  });
  protected operations = computed(() => (this.operationsResource.hasValue() ? this.operationsResource.value() : []));
  protected operationIds = computed(() => this.operations().map((op) => op.id));
  protected deletableIds = computed(() => {
    return this.operations()
      .filter((it) => it.deletable)
      .map((op) => op.id);
  });
  protected isLoading = this.operationsResource.isLoading;
  protected batchError = signal<string>(null);
  protected isSaving = signal<boolean>(false);

  protected async handleDeleteClicked() {
    this.isSaving.set(true);
    const ids = this.deletableIds();
    await Promise.all(
      ids.map(async (id) => {
        return firstValueFrom(this.ticketService.deleteOperation(id));
      }),
    )
      .then(() => {
        this.dialogRef.close();
      })
      .catch((error) => {
        this.batchError.set("Löschen fehlgeschlagen: " + (error?.message || ""));
      })
      .finally(() => {
        this.isSaving.set(false);
      });
  }

  protected async handleResetClicked() {
    this.isSaving.set(true);
    const ids = this.operationIds();
    await Promise.all(
      ids.map(async (id) => {
        const endOfYear = new Date(new Date().getFullYear(), 11, 31, 0, 0, 0);
        return firstValueFrom(
          this.operationService.updateOperation(String(id), {
            operation: {
              operation_date: endOfYear,
              technician_ids: [283], // 283 is the ID of "nicht vergeben" technician
            },
          }),
        );
      }),
    )
      .then(() => {
        this.dialogRef.close();
      })
      .catch((error) => {
        this.batchError.set("Zurücksetzen fehlgeschlagen: " + (error?.message || ""));
      })
      .finally(() => {
        this.isSaving.set(false);
      });
  }

  protected handleCancel() {
    this.dialogRef.close();
  }
}
